<template>
  <div class="right_col" role="main">
    <div class="row">
      <Header @setDriverMarkers="setDriverMarkers" />
      <div class="x_panel">
        <div class="row">
          <div ref="mapEl" class="col-md-8 col-sm-8 mapblocklarge">
            地圖正在渲染中......
          </div>
          <Form
            @followSpecificDriver="followSpecificDriver"
            @onDownloadKml="onDownloadKml"
            @setAutoLocationMarker="setAutoLocationMarker"
            @setDriverMarkerClear="setDriverMarkerClear"
          />
          <div class="col-md-4">
            <div class="x_panel">
              <div class="x_content">
                <Tab />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Header, Tab, Form } from './driver-monitor-components-v2'
import { mapState, mapActions, mapMutations } from 'vuex'
import dayjs from 'dayjs'
import { Notification } from 'element-ui'
import { reqDriverInfo, reqDispatch, reqShift } from '@/api/driverMonitor'
import { cookies } from '@/utils/cookies'
import { mapLoader } from '@/utils/googleMap'
import { formatCarYear } from '@/utils/helper'
import kmlTool from '@/assets/js/tool/kmlTool.js'
import checkStateAndDirection from '@/assets/js/tool/checkCarDirection'

const crossMarker = require('../../assets/images/crosshair.png')

export default {
  name: 'DriverMonitorV2',
  components: {
    Header,
    Tab,
    Form,
  },
  data() {
    return {
      map: null,
      driverMarkers: [],
      driverMarker: null,
      autoLocationMarker: null,
      timerId: null,
      kmlParser: null,
    }
  },
  methods: {
    ...mapMutations('driverMonitor', {
      /* set */
      vx_setSortBy: 'setSortBy',
      vx_setDriverInfo: 'setDriverInfo',
      vx_setDispatchInfo: 'setDispatchInfo',
      vx_setShiftInfo: 'setShiftInfo',
      vx_setActiveTab: 'setActiveTab',
      vx_setMapCenter: 'setMapCenter',
      /* init */
      vx_initDriverInfo: 'initDriverInfo',
      vx_initDispatchInfo: 'initDispatchInfo',
      vx_initShiftInfo: 'initShiftInfo',
    }),
    ...mapMutations('googlemap', {
      vx_setGooglemapLoader: 'setGooglemapLoader',
    }),
    ...mapActions('driverMonitor', {
      vx_fetchDriverLocations: 'fetchDriverLocations',
      vx_fetchDriverOptions: 'fetchDriverOptions',
    }),
    async searchAreaInfo(name) {
      Promise.all([reqDispatch(name), reqShift(name)])
        .then(([resDispatch, resShift]) => {
          if (resDispatch.success || resShift.success) {
            if (resDispatch.success) {
              this.vx_setDispatchInfo(resDispatch.result)
              this.vx_setActiveTab('dispatch')
            }
            if (resShift.success) {
              this.vx_setShiftInfo(resShift.result)
              this.vx_setActiveTab('shift')
            }
          } else {
            this.vx_initDispatchInfo()
            this.vx_initShiftInfo()
            Notification({
              title: `搜尋不到點擊的派遣區域`,
              type: 'warning',
            })
          }
        })
        .catch((e) => console.error(e))
    },
    setOnMap(map, marker, type) {
      try {
        switch (type) {
          case 'oneMarker':
            marker.setMap(map)
            break
          case 'multiMarker':
            marker.map((m) => m.setMap(map))
            break
          default:
            break
        }
      } catch (e) {
        console.error(e)
      }
    },
    genIcon(imgSrc, caption = undefined) {
      const parser = new DOMParser()
      let htmlStr
      if (caption) {
        htmlStr = `
          <figure>
            <img style="width: 24px; height: 24px" src="${imgSrc}" alt="icon element on map" />
            <figcaption style="color: black; font-size: 1.5rem">${caption}</figcaption>
          </figure>`
      } else {
        htmlStr = `
          <figure>
            <img style="width: 24px; height: 24px" src="${imgSrc}" alt="icon element on map" />
          </figure>`
      }
      const icon = parser.parseFromString(htmlStr, 'text/html').body.firstChild
      return icon
    },
    buildDriverInfo(driverLocation, info) {
      return {
        id: info.id,
        call_number: info.call_number,
        account: info.account,
        licence: info.licence,
        car_year: formatCarYear(info.car_year),
        name: info.name,
        car_brand: info.car_brand,
        availableSeat: info.available_seat,
        note: info.note,
        service_list: info.service_list,
        contact1: info.contact1,
        contact2: info.contact2,
        lat: driverLocation.lat,
        lng: driverLocation.lng,
        status: driverLocation.statusName,
        gender: driverLocation.gender,
        updated_at: dayjs(driverLocation.updated_at).format(
          'YYYY-MM-DD HH:mm:ss',
        ),
        team_company_note: info.team_company_note,
      }
    },
    async handleMarkerClick(driver) {
      try {
        const { success, info } = await reqDriverInfo({
          account: driver.account,
        })
        if (success) {
          const driverLocation = this.vx_driverLocations.find(
            ({ account }) => account === driver.account,
          )
          if (driverLocation) {
            const driverInfo = this.buildDriverInfo(driverLocation, info)
            this.vx_setMapCenter({
              lat: driverLocation.lat,
              lng: driverLocation.lng,
            })
            this.vx_setDriverInfo(driverInfo)
          } else {
            this.vx_initDriverInfo()
            Notification({ message: '找不到此司機', type: 'warning' })
          }
          this.vx_setActiveTab('driver')
        } else {
          Notification({
            title: `獲取司機資訊發生錯誤，請稍後再試`,
            type: 'error',
          })
        }
      } catch (e) {
        console.error(e)
        Notification({
          title: `獲取司機資訊發生錯誤，請稍後再試`,
          type: 'error',
        })
      }
    },
    async setDriverMarkers() {
      try {
        const { AdvancedMarkerElement } =
          await google.maps.importLibrary('marker')
        if (this.vx_driverLocations.length) {
          this.vx_driverLocations
            .filter((driver) => {
              if (driver.callNumber) {
                if (this.vx_sortBy) {
                  return driver.status >= 0 && driver.status < 5
                } else {
                  return true
                }
              }
            })
            .forEach((driver) => {
              const imgSrc =
                checkStateAndDirection.checkCarDirectionOutputImage(
                  driver.degrees,
                  driver.status,
                ).img
              const carIcon = this.genIcon(imgSrc, driver.callNumber)
              const marker = new AdvancedMarkerElement({
                map: this.map,
                position: { lat: driver.lat, lng: driver.lng },
                content: carIcon,
              })
              marker.data = {
                account: driver.account,
                lat: driver.lat,
                lng: driver.lng,
              }
              marker.addListener('click', () => this.handleMarkerClick(driver))
              this.driverMarkers.push(marker)
            })
          this.setOnMap(this.map, this.driverMarkers, 'multiMarker')
          if (this.vx_isTrack) {
            this.followSpecificDriver()
          }
        }
      } catch (e) {
        console.error(e)
      }
    },
    async setAutoLocationMarker() {
      try {
        if (this.driverMarker !== null) {
          this.driverMarker.setMap(null)
          this.driverMarker = null
        }
        if (this.autoLocationMarker !== null) {
          this.autoLocationMarker.setMap(null)
          this.autoLocationMarker = null
        }
        const { AdvancedMarkerElement } =
          await google.maps.importLibrary('marker')
        this.autoLocationMarker = new AdvancedMarkerElement({
          position: {
            lat: this.vx_autoLocation.latitude,
            lng: this.vx_autoLocation.longitude,
          },
          map: this.map,
          content: this.genIcon(crossMarker),
        })
        this.setOnMap(this.map, this.autoLocationMarker, 'oneMarker')
        this.map.setCenter({
          lng: this.vx_autoLocation.longitude,
          lat: this.vx_autoLocation.latitude,
        })
        this.map.setZoom(this.map.getZoom())
      } catch (e) {
        console.error(e)
      }
    },
    async followSpecificDriver() {
      try {
        if (this.vx_driverInfo.account != '' && this.driverMarkers.length) {
          this.driverMarkers.forEach((marker) => {
            if (marker.data.account == this.vx_driverInfo.account) {
              this.vx_setMapCenter({
                lat: marker.data.lat,
                lng: marker.data.lng,
              })
            }
          })
          const targetMarker = this.driverMarkers.find(
            (marker) => marker.data.account === this.vx_driverInfo.account,
          )
          if (targetMarker) {
            if (this.locationMarker) {
              this.locationMarker.setMap(null)
              this.locationMarker = null
            }
            if (this.driverMarker) {
              this.driverMarker.setMap(null)
              this.driverMarker = null
            }
            const { AdvancedMarkerElement } =
              await google.maps.importLibrary('marker')
            this.driverMarker = new AdvancedMarkerElement({
              position: {
                lat: targetMarker.data.lat,
                lng: targetMarker.data.lng,
              },
              map: this.map,
              /* icon: crossMarker, */
              content: this.genIcon(crossMarker),
            })
            this.setOnMap(this.map, this.driverMarker, 'oneMarker')
            this.map.setCenter({
              lat: targetMarker.data.lat,
              lng: targetMarker.data.lng,
            })
            this.map.setZoom(16)
          }
        }
      } catch (e) {
        console.error(e)
      }
    },
    setDriverMarkersClear() {
      this.setOnMap(null, this.driverMarkers, 'multiMarker')
      this.driverMarkers = []
    },
    setDriverMarkerClear() {
      this.setOnMap(null, this.driverMarker, 'oneMarker')
      this.driverMarker = null
    },
    /* kml */
    setKmlOnMap(map) {
      try {
        // 確保地圖物件存在且已初始化
        if (!map) {
          console.error('地圖物件不存在，無法設置 KML')
          return
        }

        this.kmlParser = new geoXML3.parser({
          map: map,
          infowindow: new google.maps.InfoWindow({}),
          singleInfoWindow: true,
          zoom: false,
          afterParse: (doc) => {
            try {
              // 確保 doc 和 placemarks 存在
              if (!doc || !doc[0] || !doc[0].placemarks) {
                console.warn('KML 解析結果中沒有 placemarks')
                return
              }

              const marks = doc[0].placemarks
              // 使用索引遍歷，以便可以延遲處理
              marks.forEach((mark, index) => {
                // 確保 polygon 和 name 存在
                if (!mark || !mark.polygon || !mark.name) {
                  return
                }

                // 延遲添加事件監聽器，確保 polygon 完全初始化
                setTimeout(() => {
                  try {
                    // 確保 polygon 物件有效且已附加到地圖
                    if (
                      mark.polygon &&
                      typeof mark.polygon.setMap === 'function'
                    ) {
                      // 檢查 polygon 是否已設置到地圖上
                      const polygonMap = mark.polygon.getMap
                        ? mark.polygon.getMap()
                        : null

                      // 如果還沒設置到地圖，先設置
                      if (polygonMap !== map) {
                        mark.polygon.setMap(map)
                      }

                      // 確保 polygon 已正確初始化後再添加監聽器
                      // 使用 try-catch 包裹，避免 __e3_ 錯誤
                      try {
                        google.maps.event.addListener(
                          mark.polygon,
                          'click',
                          () => {
                            this.searchAreaInfo(mark.name)
                          },
                        )
                      } catch (listenerError) {
                        // 如果添加監聽器失敗，可能是 polygon 還沒完全初始化
                        // 再延遲一次嘗試
                        setTimeout(() => {
                          try {
                            google.maps.event.addListener(
                              mark.polygon,
                              'click',
                              () => {
                                this.searchAreaInfo(mark.name)
                              },
                            )
                          } catch (retryError) {
                            console.error(
                              '重試添加 polygon 事件監聽器時發生錯誤:',
                              retryError,
                              mark,
                            )
                          }
                        }, 100)
                      }
                    }
                  } catch (e) {
                    console.error('添加 polygon 事件監聽器時發生錯誤:', e, mark)
                  }
                }, 50 * index) // 每個 polygon 延遲 50ms，避免同時初始化造成問題
              })
            } catch (e) {
              console.error('處理 KML placemarks 時發生錯誤:', e)
            }
          },
        })
        kmlTool.getDispatchCode()
        kmlTool.getSchualedCode()
        kmlTool.showDispatchKml(this.kmlParser)
        kmlTool.showSchualedKml(this.kmlParser)
      } catch (e) {
        console.error('setKmlOnMap 發生錯誤:', e)
      }
    },
    onDownloadKml() {
      kmlTool.clearKml()
      kmlTool.getDispatchCode()
      kmlTool.getSchualedCode()
      setTimeout(() => {
        kmlTool.showDispatchKml(this.kmlParser)
        kmlTool.showSchualedKml(this.kmlParser)
      }, 4000)
    },
    initMap() {
      const lat = parseFloat(cookies.get('GladAddressGps').coordinates[1])
      const lng = parseFloat(cookies.get('GladAddressGps').coordinates[0])
      const loader = mapLoader()
      loader
        .load()
        .then(async () => {
          try {
            await this.vx_fetchDriverLocations()
          } catch (e) {
            console.error('vx_fetchDriverLocations 發生錯誤:', e)
          }

          try {
            const { Map } = await google.maps.importLibrary('maps')
            this.map = new Map(this.$refs.mapEl, {
              mapId: 'mapId',
              zoom: 14,
              center: { lat, lng },
              streetViewControl: false,
              gestureHandling: 'greedy',
              mapTypeId: 'roadmap',
            })

            // 確保地圖完全載入後再設置 KML
            // 使用 idle 事件確保地圖完全初始化
            google.maps.event.addListenerOnce(this.map, 'idle', () => {
              try {
                // 延遲一小段時間確保地圖完全準備好
                setTimeout(() => {
                  this.setKmlOnMap(this.map)
                }, 100)
              } catch (e) {
                console.error('setKmlOnMap 發生錯誤:', e)
              }
            })
          } catch (e) {
            console.error('建立地圖實例發生錯誤:', e)
          }

          try {
            await this.vx_fetchDriverOptions()
          } catch (e) {
            console.error('vx_fetchDriverOptions 發生錯誤:', e)
          }

          try {
            this.setDriverMarkers()
          } catch (e) {
            console.error('setDriverMarkers 發生錯誤:', e)
          }
        })
        .catch((e) => {
          console.error('loader.load() 發生錯誤:', e)
        })
    },
  },
  computed: {
    ...mapState('driverMonitor', {
      vx_driverLocations: 'driverLocations',
      vx_driverInfo: 'driverInfo',
      vx_autoLocation: 'autoLocation',
      vx_sortBy: 'sortBy',
      vx_isTrack: 'isTrack',
    }),
    ...mapState('googlemap', {
      vx_googlemapLoader: 'googlemapLoader',
    }),
  },
  created() {
    this.timerId = setInterval(() => {
      this.setDriverMarkersClear()
      this.vx_fetchDriverLocations()
      this.setDriverMarkers()
    }, 15 * 1000)
  },
  mounted() {
    this.initMap()
  },
  beforeDestroy() {
    clearInterval(this.timerId)
    this.timerId = null
    this.map = null
  },
}
</script>

<style scoped>
input.inputstyle {
  min-width: 240px;
}

.x_panel_customerize {
  width: 100%;
  padding: 5px 5px;
  display: inline-block;
  background: #fff;
  border: 1px solid #e6e9ed;
  -webkit-column-break-inside: avoid;
  opacity: 1;
}
</style>



// WEBPACK FOOTER //
// src/pages/driver/DriverMonitorV2.vue